import { z } from 'zod';
import { PasswordSchema } from './schemas/password.schema.js';
// User login validation
export const LoginUserSchema = z.object({
    username: z.string().min(1, 'Username is required'),
    password: z.string().min(1, 'Password is required')
});
// Valid character IDs for avatar validation
export const VALID_CHARACTER_IDS = [
    'premade_01', 'premade_02', 'premade_03', 'premade_04', 'premade_05',
    'premade_06', 'premade_07', 'premade_08', 'premade_09', 'premade_10',
    'premade_11', 'premade_12', 'premade_13', 'premade_14', 'premade_15',
    'premade_16', 'premade_17', 'premade_18', 'premade_19', 'premade_20'
];
// User profile update validation
export const UpdateUserSchema = z.object({
    username: z
        .string()
        .min(3, 'Username must be at least 3 characters')
        .max(50, 'Username must be at most 50 characters')
        .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
        .optional(),
    email: z.string().email('Invalid email format').max(255).optional(),
    avatar: z.enum(VALID_CHARACTER_IDS, {
        errorMap: () => ({ message: 'Invalid character selection' })
    }).optional(),
    currentPassword: z.string().min(1).optional(),
    newPassword: PasswordSchema.optional()
}).refine((data) => {
    // If newPassword is provided, currentPassword must also be provided
    if (data.newPassword && !data.currentPassword) {
        return false;
    }
    return true;
}, {
    message: 'Current password is required when changing password',
    path: ['currentPassword']
});
// Error types
export class ApiError extends Error {
    statusCode;
    code;
    constructor(statusCode, message, code) {
        super(message);
        this.statusCode = statusCode;
        this.code = code;
        this.name = 'ApiError';
    }
}
// ========================================
// MULTIPLAYER TYPES AND SCHEMAS
// ========================================
// Enum types
export var RoomRole;
(function (RoomRole) {
    RoomRole["ADMIN"] = "admin";
    RoomRole["PLAYER"] = "player";
    /** Time-boxed island access; the permission always carries an `expiresAt`. */
    RoomRole["GUEST"] = "guest";
})(RoomRole || (RoomRole = {}));
/** Platform-wide role, independent of any island role. */
export var PlatformRole;
(function (PlatformRole) {
    PlatformRole["USER"] = "user";
    PlatformRole["SUPERADMIN"] = "superadmin";
})(PlatformRole || (PlatformRole = {}));
/** Guest access durations, in days, offered when inviting or extending a guest. */
export const GUEST_DAY_OPTIONS = [1, 7, 30];
export const GuestDaysSchema = z.union([z.literal(1), z.literal(7), z.literal(30)], {
    errorMap: () => ({ message: `Guest days must be one of ${GUEST_DAY_OPTIONS.join(', ')}` })
});
/** Superadmin console lists return at most this many rows plus `truncated`. */
export const ADMIN_LIST_LIMIT = 500;
export var PlayerStatus;
(function (PlayerStatus) {
    PlayerStatus["AVAILABLE"] = "available";
    PlayerStatus["BUSY"] = "busy";
    PlayerStatus["DND"] = "dnd";
})(PlayerStatus || (PlayerStatus = {}));
/** Organization names are unique case-insensitively and at most 100 characters. */
export const ORGANIZATION_NAME_MAX_LENGTH = 100;
// Room validation schemas
/**
 * Creating an island always names its organization (§3.1 invariant 1): either
 * an existing one the creator may create in (§4.2), or a brand new one. Exactly
 * one of the two fields is accepted — naming both, or neither, is a 400.
 */
export const CreateRoomSchema = z
    .object({
    name: z
        .string()
        .min(1, 'Room name is required')
        .max(100, 'Room name must be at most 100 characters'),
    description: z
        .string()
        .max(500, 'Description must be at most 500 characters')
        .optional(),
    maxPlayers: z
        .number()
        .int('Max players must be an integer')
        .min(1, 'Room must allow at least 1 player')
        .max(100, 'Room cannot exceed 100 players')
        .optional()
        .default(100),
    organizationId: z.string().uuid('Invalid organization ID').optional(),
    newOrganizationName: z
        .string()
        .trim()
        .min(1, 'Organization name is required')
        .max(ORGANIZATION_NAME_MAX_LENGTH, `Organization name must be at most ${ORGANIZATION_NAME_MAX_LENGTH} characters`)
        .optional()
})
    .refine(data => (data.organizationId ? 1 : 0) + (data.newOrganizationName ? 1 : 0) === 1, { message: 'Choose an organization or name a new one', path: ['organizationId'] });
/**
 * Island settings an admin may edit. The organization is deliberately absent:
 * only superadmins move an island, through the console (§4.2).
 */
export const UpdateRoomSchema = z.strictObject({
    name: z
        .string()
        .min(1, 'Room name is required')
        .max(100, 'Room name must be at most 100 characters')
        .optional(),
    description: z
        .string()
        .max(500, 'Description must be at most 500 characters')
        .optional(),
    maxPlayers: z
        .number()
        .int('Max players must be an integer')
        .min(1, 'Room must allow at least 1 player')
        .max(100, 'Room cannot exceed 100 players')
        .optional()
});
/**
 * Permission update: either a new role, or an extension of a guest's access.
 * Exactly one shape is accepted; `'role' in input` discriminates them.
 */
export const UpdatePermissionSchema = z.union([
    // Strict on both sides: `{ role, extendGuestDays }` asks for two different
    // changes at once, and silently honouring one of them is worse than a 400.
    z.strictObject({
        role: z.nativeEnum(RoomRole, { required_error: 'Role is required' })
    }),
    z.strictObject({
        extendGuestDays: GuestDaysSchema
    })
], {
    errorMap: () => ({
        message: `Provide either role or extendGuestDays (${GUEST_DAY_OPTIONS.join(', ')})`
    })
});
/** Superadmin retargeting an island to another organization. */
export const SetRoomOrganizationSchema = z.object({
    organizationId: z.string().uuid('Invalid organization ID')
});
// WebSocket message schemas
export const PlayerMovementSchema = z.object({
    position: z.object({
        x: z.number(),
        y: z.number()
    }),
    velocity: z.object({
        x: z.number(),
        y: z.number()
    }).optional(),
    animation: z.string(),
    direction: z.enum(['up', 'down', 'left', 'right']),
    seated: z.boolean().optional(),
    decorationId: z.string().optional()
});
export const JoinRoomMessageSchema = z.object({
    roomId: z.string().uuid('Invalid room ID')
});
export const UserStatusChangeSchema = z.object({
    roomId: z.string().uuid('Invalid room ID'),
    status: z.enum(['available', 'busy', 'dnd'])
});
export const PlayerAvatarUpdateSchema = z.object({
    roomId: z.string().uuid('Invalid room ID'),
    avatar: z.enum(VALID_CHARACTER_IDS, {
        errorMap: () => ({ message: 'Invalid character selection' })
    })
});
export const PlayerTeleportSchema = z.object({
    targetUserId: z.string().uuid('Invalid user ID'),
    position: z.object({
        x: z.number(),
        y: z.number()
    }),
    roomId: z.string().uuid('Invalid room ID').optional()
});
// ========================================
// HOME ZONE TYPES AND SCHEMAS
// ========================================
// Zone status enum
export var ZoneStatus;
(function (ZoneStatus) {
    ZoneStatus["UNCLAIMED"] = "UNCLAIMED";
    ZoneStatus["CLAIMED"] = "CLAIMED";
})(ZoneStatus || (ZoneStatus = {}));
// Zone claiming validation
export const ClaimZoneSchema = z.object({
    zoneId: z.string().min(1, 'Zone ID is required')
});
